import { defaultStorefrontAppearance, type StorefrontAppearance } from "../storefront-appearance";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { companyAPI, paymentMethodsAPI } from "@/lib/api";
import { COMPANY_PROFILE_QUERY_KEY } from "@/hooks/useCompanyProfile";

export const storefrontContentKeys = [
	"supportPhone",
	"supportEmail",
	"supportWhatsapp",
	"supportWebsite",
	"supportAddress",
	"aboutText",
	"faqText",
	"termsText",
	"privacyText",
] as const;
export type StorefrontContentKey = (typeof storefrontContentKeys)[number];
type Draft = {
	appearance: StorefrontAppearance;
	paymentMethodIds: number[];
	content: Record<StorefrontContentKey, string>;
};
type PaymentMethod = { id: number; code: string; description?: string | null; iconUrl?: string };
const emptyDraft = (): Draft => ({
	appearance: { ...defaultStorefrontAppearance },
	paymentMethodIds: [],
	content: Object.fromEntries(storefrontContentKeys.map(key => [key, ""])) as Draft["content"],
});

/** Relocates the existing company-wide storefront editor without changing its storage scope. */
export function useStorefrontSettings(enabled: boolean, scopeKey?: string) {
	const queryClient = useQueryClient();
	const [pendingUploads, setPendingUploads] = useState(0);
	const [draft, setDraft] = useState<Draft>(emptyDraft);
	const [saved, setSaved] = useState<Draft>(emptyDraft);
	const [profile, setProfile] = useState<any>(null);
	const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
	const [paymentError, setPaymentError] = useState(false);
	const [loading, setLoading] = useState(enabled);
	const [error, setError] = useState(false);
	const [revision, setRevision] = useState(0);
	useEffect(() => {
		if (!enabled) return;
		let cancelled = false;
		setLoading(true);
		setError(false);
		Promise.all([companyAPI.getProfile(), paymentMethodsAPI.getAll().catch(() => null)])
			.then(([profile, methods]) => {
				if (cancelled) return;
				setProfile(profile);
				const settings = profile.settings ?? {};
				const content = Object.fromEntries(
					storefrontContentKeys.map(key => {
						const value = settings[`storefront${key[0].toUpperCase()}${key.slice(1)}`];
						return [key, typeof value === "string" ? value : ""];
					}),
				) as Draft["content"];
				const next = {
					appearance: { ...defaultStorefrontAppearance, ...(settings.storefrontAppearance || {}) },
					content,
					paymentMethodIds: Array.isArray(settings.storefrontPaymentMethodIds)
						? settings.storefrontPaymentMethodIds.map(Number).filter(Number.isFinite)
						: [],
				};
				setDraft(next);
				setSaved(next);
				setPaymentMethods(methods ?? []);
				setPaymentError(methods === null);
			})
			.catch(() => {
				if (!cancelled) setError(true);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [enabled, scopeKey, revision]);

	const dirty = enabled && !loading && !error && JSON.stringify(draft) !== JSON.stringify(saved);
	const save = async () => {
		if (pendingUploads) throw new Error("Images are still uploading");
		if (!enabled || loading || error || !dirty) return;
		// Do not write unrelated settings or clear payment IDs when the method list is unavailable.
		if (JSON.stringify(draft.paymentMethodIds) !== JSON.stringify(saved.paymentMethodIds)) {
			await companyAPI.updateStorefrontPaymentMethods(draft.paymentMethodIds);
			setSaved(previous => ({ ...previous, paymentMethodIds: draft.paymentMethodIds }));
		}
		const contentChanged = JSON.stringify(draft.content) !== JSON.stringify(saved.content);
		const appearanceChanged = JSON.stringify(draft.appearance) !== JSON.stringify(saved.appearance);
		if (contentChanged || appearanceChanged) {
			await companyAPI.updateStorefrontContent({
				...(contentChanged ? draft.content : {}),
				...(appearanceChanged ? { appearance: draft.appearance } : {}),
			});
			setSaved(previous => ({ ...previous, content: draft.content, appearance: draft.appearance }));
		}
		await queryClient.invalidateQueries({ queryKey: COMPANY_PROFILE_QUERY_KEY });
	};
	return {
		pendingUploads,
		beginUpload: () => setPendingUploads(count => count + 1),
		endUpload: () => setPendingUploads(count => Math.max(0, count - 1)),
		profile,
		draft,
		setDraft,
		paymentMethods,
		paymentError,
		loading,
		error,
		dirty,
		save,
		reset: () => setDraft(saved),
		retry: () => setRevision(value => value + 1),
	};
}
export type StorefrontSettingsModel = ReturnType<typeof useStorefrontSettings>;
